import {
  FunctionComponent,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  Dispatch,
  useEffect,
  ReactNode,
  SetStateAction,
} from 'react'
import type {
  OnlineOperationalMenuItem,
  OnlineOperationalMenuRefItem,
  OperationalMenu,
} from '@/libs/helpers/adapters'
import type { CartChildItem } from '@/libs/helpers/utils'

export const MAX_NESTED_MODS_DEPTH = 5

type ModifierValidationError = 'MIN_SELECTION' | 'MAX_SELECTION' | 'NESTED_MODIFIER'
export type ModifierValidationErrors = {
  [modifierGroupXRefID: string]: {
    error: ModifierValidationError
    childItems?: { [modifierOptionXRefID: string]: ModifierValidationErrors }
  }
}
export type NestedModifiersTrace = { modifierGroupXRefID: string; itemID: string }

export interface ContextType {
  /**
   * only for non-nested modifiers use
   */
  setModifiers: Dispatch<SetStateAction<CartChildItem[]>>
  /**
   * modifiers at the top modifier level
   */
  modifiers: CartChildItem[]
  /**
   * modifiers at the current nested modifier level
   */
  currentModifiers: CartChildItem[]
  setMenuItem: Dispatch<SetStateAction<OnlineOperationalMenuItemCtxPayload | null>>
  setItemIndex: Dispatch<SetStateAction<number>>
  /**
   * errors at the top modifier level
   */
  errors: ModifierValidationErrors
  /**
   * errors at the current nested modifier level
   */
  currentErrors: ModifierValidationErrors
  menuItem: OnlineOperationalMenuItemCtxPayload | null
  itemIndex: number
  shouldDisplayErrors: boolean
  setShouldDisplayErrors: Dispatch<SetStateAction<boolean>>
  nextRequiredModifierGroupID?: string | null
  validateModifiers: () => void
  openedNestedModifier: NestedModifiersTrace | null
  openNestedModifiersTrace: NestedModifiersTrace[]
  openNestedModifier: (modifier: NestedModifiersTrace) => void
  closeNestedModifier: () => void
  resetContext: () => void
  updateQuantity: (
    quantityUpdates: { cartChildItem: Omit<CartChildItem, 'quantity'>; quantity: number }[]
  ) => void
  replaceTheTrace: (newTrace: NestedModifiersTrace[]) => void
}

export interface OnlineOperationalMenuItemCtxPayload extends OnlineOperationalMenuItem {
  menuGroupXRefID: string
}

type UseHookType = () => ContextType

const MenuItemContext = createContext<ContextType | null>(null)

const sumQuantityChildItems = (childItems: CartChildItem[]): number => {
  let sum = 0
  for (const childItem of childItems) {
    sum += Math.floor(Number(childItem.quantity))
  }
  return sum
}

const Provider: FunctionComponent<{
  operationalMenu: OperationalMenu
  hasNestedMods: boolean
  children?: ReactNode
}> = ({ operationalMenu, children, hasNestedMods }) => {
  const [shouldDisplayErrors, setShouldDisplayErrors] = useState<boolean>(false)
  const [openNestedModifiersTrace, setOpenNestedModifiersTrace] = useState<NestedModifiersTrace[]>(
    []
  )
  const [modifiers, setModifiers] = useState<CartChildItem[]>([])
  const [currentModifiers, _setCurrentModifiers] = useState<CartChildItem[]>([])
  const [errors, setErrors] = useState<ModifierValidationErrors>({})
  const [currentErrors, _setCurrentErrors] = useState<ModifierValidationErrors>({})
  const [menuItem, setMenuItem] = useState<OnlineOperationalMenuItemCtxPayload | null>(null)
  const [itemIndex, setItemIndex] = useState<number>(-1)
  const [nextRequiredModifierGroupID, setNextRequiredModifierGroupID] = useState<string | null>(
    null
  )
  const openedNestedModifier = useMemo(() => {
    if (openNestedModifiersTrace.length > 0) {
      return openNestedModifiersTrace[openNestedModifiersTrace.length - 1]
    } else {
      return null
    }
  }, [openNestedModifiersTrace])

  const resetContext = useCallback(() => {
    // Resets while closing
    setShouldDisplayErrors(false)
    setErrors({})
    setMenuItem(null)
    setModifiers([])
    setItemIndex(-1)
    setOpenNestedModifiersTrace([])
    _setCurrentErrors({})
    _setCurrentModifiers([])
  }, [])

  const setCurrentNestedModContext = useCallback(() => {
    let currentErrorNode = errors
    let currentModifiersNode = modifiers ?? []
    if (openNestedModifiersTrace.length > 0) {
      for (const currentNestedModifier of openNestedModifiersTrace) {
        // traverse the error tree
        if (Object.entries(currentErrorNode).length > 0) {
          const node = currentErrorNode[currentNestedModifier.modifierGroupXRefID]
          if (node && node.childItems && node.childItems[currentNestedModifier.itemID]) {
            currentErrorNode = node.childItems[currentNestedModifier.itemID]
          } else {
            currentErrorNode = {}
          }
        }
        // traverse the child items tree
        if (currentModifiersNode.length > 0) {
          const node = currentModifiersNode.find(
            (modifier) =>
              modifier.modifierGroupXRefID === currentNestedModifier.modifierGroupXRefID &&
              modifier.itemID === currentNestedModifier.itemID
          )
          if (node && node.childItems) {
            currentModifiersNode = node.childItems
          } else if (node) {
            node.childItems = []
            currentModifiersNode = node.childItems
          } else {
            currentModifiersNode = []
          }
        }
      }
    }
    _setCurrentErrors({ ...currentErrorNode })
    _setCurrentModifiers([...currentModifiersNode])
  }, [errors, modifiers, openNestedModifiersTrace])

  useEffect(() => {
    if (hasNestedMods) {
      setCurrentNestedModContext()
    } else {
      _setCurrentErrors({ ...errors })
      _setCurrentModifiers([...modifiers])
    }
  }, [modifiers, setCurrentNestedModContext, hasNestedMods, errors])

  const openNestedModifier = useCallback(
    (modifier: NestedModifiersTrace): void => {
      setOpenNestedModifiersTrace([...openNestedModifiersTrace, modifier])
      setCurrentNestedModContext()
    },
    [openNestedModifiersTrace, setCurrentNestedModContext]
  )

  const closeNestedModifier = useCallback((): void => {
    if (openNestedModifiersTrace.length > 0) {
      setOpenNestedModifiersTrace(openNestedModifiersTrace.slice(0, -1))
      setCurrentNestedModContext()
    }
  }, [openNestedModifiersTrace, setCurrentNestedModContext])

  const replaceTheTrace = useCallback(
    (newTrace: NestedModifiersTrace[]) => {
      setOpenNestedModifiersTrace(newTrace)
      setCurrentNestedModContext()
    },
    [setCurrentNestedModContext]
  )

  const validateModifiersV2 = useCallback(
    (
      childItems: CartChildItem[],
      menuItemGroupRefs: OnlineOperationalMenuRefItem[],
      depth: number
    ): ModifierValidationErrors => {
      const res: ModifierValidationErrors = {}
      for (const modifierGroupRef of menuItemGroupRefs) {
        const modifierGroup = operationalMenu.modifierGroups[modifierGroupRef.id]
        if (!modifierGroup) {
          continue
        }
        const groupChildItems = childItems.filter(
          (childItem) => childItem.modifierGroupXRefID === modifierGroup.XRefID
        )
        const quantity = sumQuantityChildItems(groupChildItems)
        if (
          quantity > modifierGroup.selectionMax &&
          modifierGroup.selectionMax > 0 &&
          !res[modifierGroup.XRefID]
        ) {
          res[modifierGroup.XRefID] = { error: 'MAX_SELECTION' }
        }
        if (
          quantity < modifierGroup.selectionMin &&
          modifierGroup.selectionMin > 0 &&
          !res[modifierGroup.XRefID]
        ) {
          res[modifierGroup.XRefID] = { error: 'MIN_SELECTION' }
        }
        if (hasNestedMods && depth + 1 <= MAX_NESTED_MODS_DEPTH) {
          for (const childItem of groupChildItems) {
            const modifierOption = operationalMenu.modifierOptions[childItem.itemID]
            // INSTRUCTION MODIFIER or missing option
            if (!modifierOption || !modifierOption.menuItemXRefID) {
              continue
            }
            const menuItem = operationalMenu.menuItems[modifierOption.menuItemXRefID]
            if (!menuItem) {
              continue
            }
            const nestedRes = validateModifiersV2(
              childItem.childItems ?? [],
              menuItem.modifierGroupRefs,
              depth + 1
            )
            if (Object.keys(nestedRes).length > 0) {
              if (!res[modifierGroup.XRefID]) {
                res[modifierGroup.XRefID] = { error: 'NESTED_MODIFIER' }
              }
              const temp = res[modifierGroup.XRefID]
              if (!temp.childItems) {
                temp.childItems = { [childItem.itemID]: nestedRes }
              } else {
                temp.childItems[childItem.itemID] = nestedRes
              }
            }
          }
        }
      }
      return res
    },
    [hasNestedMods, operationalMenu]
  )

  const validateModifiers = useCallback((): void => {
    if (menuItem == null || !modifiers) {
      return
    }
    const childItems = modifiers ?? []
    const availableModifierGroups = menuItem.modifierGroupRefs
    const v2ValidationRes = validateModifiersV2(childItems, availableModifierGroups, 1)
    setNextRequiredModifierGroupID(null)
    setErrors(v2ValidationRes)
    for (const modifierGroupRef of menuItem.modifierGroupRefs) {
      if (v2ValidationRes[modifierGroupRef.id] != null) {
        setNextRequiredModifierGroupID(modifierGroupRef.id)
        break
      }
    }
  }, [menuItem, modifiers, validateModifiersV2])

  /**
   * @param quantity a new quantity of an item, 0 to remove
   */
  const updateQuantity = useCallback(
    (
      modifierUpdates: { cartChildItem: Omit<CartChildItem, 'quantity'>; quantity: number }[]
    ): void => {
      const newModifiers = [...modifiers]
      let currentNode = newModifiers
      for (const currentNestedModifier of openNestedModifiersTrace) {
        // traverse the child items tree
        if (currentNode.length > 0) {
          const node = currentNode.find(
            (modifier) =>
              modifier.modifierGroupXRefID === currentNestedModifier.modifierGroupXRefID &&
              modifier.itemID === currentNestedModifier.itemID
          )
          if (node && node.childItems) {
            currentNode = node.childItems
          } else if (node) {
            node.childItems = []
            currentNode = node.childItems
          } else {
            return
          }
        }
      }
      for (const { cartChildItem, quantity } of modifierUpdates) {
        const newQuantity = Math.max(Math.floor(quantity), 0)
        const modifier = currentNode.find(
          (modifier) =>
            modifier.modifierGroupXRefID === cartChildItem.modifierGroupXRefID &&
            modifier.itemID === cartChildItem.itemID
        )
        const newModifier = {
          ...cartChildItem,
          quantity: newQuantity.toFixed(2),
        }
        if (!modifier && quantity > 0) {
          currentNode.push(newModifier)
        } else if (modifier && quantity > 0) {
          modifier.quantity = quantity.toFixed(2)
        } else if (modifier && quantity === 0) {
          const idx = currentNode.findIndex(
            (modifier) =>
              modifier.modifierGroupXRefID === cartChildItem.modifierGroupXRefID &&
              modifier.itemID === cartChildItem.itemID
          )
          if (idx >= 0) {
            currentNode.splice(idx, 1)
          }
        }
      }
      setModifiers(newModifiers)
      setCurrentNestedModContext()
      // modifying quantity dismisses error display
      setShouldDisplayErrors(false)
    },
    [modifiers, openNestedModifiersTrace, setCurrentNestedModContext]
  )

  const ctxValue = useMemo(
    () => ({
      setShouldDisplayErrors,
      shouldDisplayErrors,
      setModifiers,
      setItemIndex,
      setMenuItem,
      openNestedModifiersTrace,
      modifiers,
      itemIndex,
      menuItem,
      errors,
      nextRequiredModifierGroupID,
      validateModifiers,
      openedNestedModifier,
      openNestedModifier,
      closeNestedModifier,
      resetContext,
      currentErrors,
      currentModifiers,
      updateQuantity,
      replaceTheTrace,
    }),
    [
      setShouldDisplayErrors,
      shouldDisplayErrors,
      setModifiers,
      setItemIndex,
      setMenuItem,
      openNestedModifiersTrace,
      modifiers,
      itemIndex,
      menuItem,
      errors,
      nextRequiredModifierGroupID,
      validateModifiers,
      openedNestedModifier,
      openNestedModifier,
      closeNestedModifier,
      resetContext,
      currentErrors,
      currentModifiers,
      updateQuantity,
      replaceTheTrace,
    ]
  )

  useEffect(() => {
    // All the modifier quantity validation magic happens here!!!!!
    validateModifiers()
  }, [validateModifiers])

  return <MenuItemContext.Provider value={ctxValue}>{children}</MenuItemContext.Provider>
}

export default Provider
export const useMenuItem: UseHookType = () => {
  const ctx = useContext(MenuItemContext)

  if (!ctx) {
    throw new Error('useMenuItem must be used within Provider')
  }

  return ctx
}
